/**
 * Admin 「외부 연동」 — 파트너 상세 `/admin/partners/:id`.
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — 「화면 설계 › 파트너 상세」, D-2·D-3·D-9·D-10·D-11
 *
 * 비즈니스 로직(조회·변경·확인 창 열기)은 여기서, 표시는 DetailSections·각 모달에 맡긴다.
 * 발급·서명 키 생성이 성공하면 원문 표시 창(showSecretReveal)을 연다.
 */
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "components/atoms/Button";
import { ConfirmManager, ToastManager } from "components/organisms/CommonUI";
import type { ApiKeyView, PartnerMeta, PartnerSummary } from "api/partnerAdminService";
import { usePartnerDetail, usePartnerMeta, usePartnerMutations } from "hooks/partners/usePartners";
import { buildUpdateBody, isRelEnv } from "utils/partnerAdmin";
import EnvBadge from "./EnvBadge";
import PartnerFormModal from "./PartnerFormModal";
import ApiKeyFormModal from "./ApiKeyFormModal";
import { GenerateSecretModal, WebhookSecretInputModal } from "./WebhookSecretModals";
import { ISSUE_RAW_STORED_NOTICE, showSecretReveal } from "./SecretRevealModal";
import { RawKeyRegisterModal, requestRevealCopy } from "./RawKeyModals";
import { ApiKeySection, EventHistory, PartnerInfoSection, WebhookSecretSection } from "./DetailSections";
import { showConfirm } from "./confirmDialog";
import { BackLink, EmptyText, Notice, PageHeader, PageTitle, PageWrap, TitleRow } from "./style";

const FORM_OPTIONS = { showBackdrop: true, backdropClose: false, showCloseButton: false } as const;

const PartnerDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const partnerId = Number(id);
  const meta = usePartnerMeta();
  const detail = usePartnerDetail(partnerId);
  const m = usePartnerMutations(partnerId);

  // [원문 복사] 요청 중인 키(연타 방지)와, 복호화·불일치로 [원문 등록]을 보조로 열어 둘 키들.
  const [revealingKeyId, setRevealingKeyId] = useState<number | null>(null);
  const [recoverKeyIds, setRecoverKeyIds] = useState<ReadonlySet<number>>(() => new Set());

  const env = meta.data?.env;
  const isRel = isRelEnv(env);

  const header = (
    <PageHeader>
      <TitleRow>
        <BackLink
          href="/admin/partners"
          onClick={(e) => {
            e.preventDefault();
            navigate("/admin/partners");
          }}
        >
          외부 연동
        </BackLink>
        <span>/</span>
        <PageTitle>{detail.data?.partner.name ?? "파트너"}</PageTitle>
        <EnvBadge env={env} />
      </TitleRow>
    </PageHeader>
  );

  if (!Number.isInteger(partnerId) || partnerId <= 0) {
    return (
      <PageWrap>
        {header}
        <Notice $tone="warn">잘못된 주소입니다.</Notice>
      </PageWrap>
    );
  }
  // meta(환경·암호화 준비)를 받기 전에는 버튼을 열지 않는다 — 운영(REL) 확인 문구(D-9)가 빠진 채
  // 발급·폐기 창이 뜨지 않게 하기 위해서다.
  if (detail.isLoading || meta.isLoading) {
    return (
      <PageWrap>
        {header}
        <EmptyText>불러오는 중입니다.</EmptyText>
      </PageWrap>
    );
  }
  if (meta.isError || !meta.data) {
    return (
      <PageWrap>
        {header}
        <Notice $tone="warn">
          {meta.error?.message ?? "외부 연동 설정 정보를 불러오지 못했습니다."}{" "}
          <Button type="Text" size="XSmall" variant="Ghost" color="Secondary" onClick={() => meta.refetch()}>
            다시 불러오기
          </Button>
        </Notice>
      </PageWrap>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <PageWrap>
        {header}
        <Notice $tone="warn">
          {detail.error?.message ?? "파트너 정보를 불러오지 못했습니다."}{" "}
          <Button type="Text" size="XSmall" variant="Ghost" color="Secondary" onClick={() => detail.refetch()}>
            다시 불러오기
          </Button>
        </Notice>
      </PageWrap>
    );
  }

  const { partner, keys, events } = detail.data;
  const canProvision = !!partner.provisionUrl;

  // ① 기본 정보 ---------------------------------------------------------------

  const openEdit = (p: PartnerSummary, metaData: PartnerMeta) => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <PartnerFormModal
          meta={metaData}
          partner={p}
          onSubmit={async (values) => {
            const body = buildUpdateBody(p, values);
            if (Object.keys(body).length === 0) {
              ToastManager.show({ content: "바뀐 내용이 없습니다.", duration: 2000 });
              ConfirmManager.hide();
              return;
            }
            await m.update.mutateAsync(body);
            ConfirmManager.hide();
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  const toggleActive = () => {
    if (partner.isActive) {
      showConfirm({
        title: "파트너 비활성",
        lines: [
          `파트너 「${partner.name}」: 비활성으로 바꿉니다.`,
          "이후 들어오는 요청은 거절합니다. 이미 접수된 건의 결과 통지·자동 분석은 계속됩니다.",
        ],
        isRel,
        confirmLabel: "비활성",
        negative: true,
        onConfirm: () => m.setActive.mutateAsync(false),
      });
    } else {
      showConfirm({
        title: "파트너 활성",
        lines: [`파트너 「${partner.name}」: 다시 활성으로 바꿉니다. 이 파트너의 요청을 다시 받습니다.`],
        isRel: false,
        confirmLabel: "활성",
        onConfirm: () => m.setActive.mutateAsync(true),
      });
    }
  };

  // ② 연동 키 -----------------------------------------------------------------

  const openIssue = () => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <ApiKeyFormModal
          canProvision={canProvision}
          isRel={isRel}
          onSubmit={async (values) => {
            const result = await m.issueKey.mutateAsync(values);
            // 원문이 보관됐으면(rawStored) 「나중에 [원문 복사]로」, 아니면 기존 「한 번만 보임」 문구.
            showSecretReveal({
              title: "연동 키 원문",
              raw: result.rawKey,
              provision: result.provision,
              notice: result.rawStored ? ISSUE_RAW_STORED_NOTICE : undefined,
            });
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  const openKeyEdit = (k: ApiKeyView) => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <ApiKeyFormModal
          apiKey={k}
          canProvision={false}
          isRel={isRel}
          onSubmit={async (values) => {
            await m.updateKey.mutateAsync({
              keyId: k.id,
              body: { label: values.label, ipAllowlist: values.ipAllowlist },
            });
            ConfirmManager.hide();
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  const revokeKey = (k: ApiKeyView) => {
    showConfirm({
      title: "연동 키 폐기",
      lines: [
        `「${k.label ?? k.fingerprint}」(${k.fingerprint}) 키를 폐기합니다.`,
        "다음 요청부터 이 키로 호출할 수 없습니다. 폐기는 되돌릴 수 없습니다.",
      ],
      isRel,
      confirmLabel: "폐기",
      negative: true,
      onConfirm: () => m.revokeKey.mutateAsync(k.id),
    });
  };

  // [원문 복사] — reveal로 받은 원문을 클립보드에만 넣는다(화면에 렌더하지 않음). REL이면 확인 창을 거친다.
  // 요청 중에는 그 행 버튼을 막고, 복호화·불일치면 그 행에 [원문 등록]을 보조로 연다(D10·D9).
  const revealKey = (k: ApiKeyView) => {
    requestRevealCopy({
      isRel,
      reveal: () => m.revealKey.mutateAsync(k.id),
      onStart: () => setRevealingKeyId(k.id),
      onSettled: () => setRevealingKeyId((cur) => (cur === k.id ? null : cur)),
      onRecoverable: () => setRecoverKeyIds((prev) => new Set(prev).add(k.id)),
    });
  };

  // [원문 등록] — 원문이 없는 기존 키에 원문을 넣는다. 성공하면 상세를 다시 불러온다(훅 handlers).
  const registerRawKey = (k: ApiKeyView) => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <RawKeyRegisterModal
          fingerprint={k.fingerprint}
          onSubmit={async (rawKey) => {
            await m.registerRawKey.mutateAsync({ keyId: k.id, rawKey });
            ConfirmManager.hide();
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  // ③ 웹훅 서명 키 -------------------------------------------------------------

  const openSecretInput = () => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <WebhookSecretInputModal
          isRel={isRel}
          onSubmit={async (secret) => {
            await m.setSecret.mutateAsync(secret);
            ConfirmManager.hide();
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  const openSecretGenerate = () => {
    ConfirmManager.show({
      ...FORM_OPTIONS,
      body: (
        <GenerateSecretModal
          canProvision={canProvision}
          isRel={isRel}
          onSubmit={async (provision) => {
            const result = await m.generateSecret.mutateAsync(provision);
            showSecretReveal({ title: "웹훅 서명 키 원문", raw: result.rawSecret, provision: result.provision });
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
    });
  };

  const clearSecret = () => {
    showConfirm({
      title: "서명 키 DB 값 지우기",
      lines: ["DB 값을 지우면 환경변수·코드 상수 값으로 돌아갑니다"],
      isRel,
      confirmLabel: "지우기",
      negative: true,
      onConfirm: () => m.clearSecret.mutateAsync(),
    });
  };

  return (
    <PageWrap data-testid="partner-detail-page">
      {header}
      <PartnerInfoSection
        partner={partner}
        meta={meta.data}
        onEdit={() => meta.data && openEdit(partner, meta.data)}
        onToggleActive={toggleActive}
      />
      <ApiKeySection
        keys={keys}
        onIssue={openIssue}
        onEdit={openKeyEdit}
        onRevoke={revokeKey}
        onReveal={revealKey}
        onRegisterRaw={registerRawKey}
        encryptionReady={meta.data?.encryptionReady}
        revealingKeyId={revealingKeyId}
        recoverKeyIds={recoverKeyIds}
      />
      <WebhookSecretSection
        secret={partner.webhookSecret}
        encryptionReady={meta.data?.encryptionReady}
        onInput={openSecretInput}
        onGenerate={openSecretGenerate}
        onClear={clearSecret}
      />
      <EventHistory events={events} keys={keys} />
    </PageWrap>
  );
};

export default PartnerDetailPage;
